import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, UserCheck, ShieldCheck, Ban } from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { getAdminUsuariosStats } from "../../api";
import SideBarNavUsuarios from "../../componets/SideBarNavUsuarios";

const MESES_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const ROLE_CORES = ["#7C3AED", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#06B6D4", "#84CC16"];
const STATUS_CORES = { Ativos: "#10B981", Bloqueados: "#EF4444", Inativos: "#94A3B8" };

function KpiCard({ icon: Icon, label, value, cor }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className="p-3 rounded-xl" style={{ background: `${cor}1A` }}>
        <Icon className="w-5 h-5" style={{ color: cor }} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

export default function UsuariosDashboard() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErro(null);
      try {
        setDados(await getAdminUsuariosStats());
      } catch (e) {
        setErro(e.message || "Acesso não autorizado.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const usuarios = useMemo(() => dados?.usuarios ?? [], [dados]);
  const resumo = useMemo(() => dados?.resumo ?? {}, [dados]);

  const loginsPorMes = useMemo(() => {
    const base = resumo.logins_por_mes ?? [];
    return base.map((item) => ({ mes: MESES_PT[item.mes - 1], total: item.total }));
  }, [resumo]);

  const porRole = useMemo(() => {
    const mapa = new Map();
    for (const u of usuarios) {
      const chave = u.role || "Sem role";
      mapa.set(chave, (mapa.get(chave) || 0) + 1);
    }
    return Array.from(mapa.entries())
      .map(([role, qtd]) => ({ role, qtd }))
      .sort((a, b) => b.qtd - a.qtd);
  }, [usuarios]);

  const porStatus = useMemo(() => {
    let ativos = 0, bloqueados = 0, inativos = 0;
    for (const u of usuarios) {
      if (u.bloqueado) bloqueados++;
      else if (u.ativo) ativos++;
      else inativos++;
    }
    return [
      { status: "Ativos", qtd: ativos },
      { status: "Bloqueados", qtd: bloqueados },
      { status: "Inativos", qtd: inativos },
    ].filter((s) => s.qtd > 0);
  }, [usuarios]);

  const alturaGraficoRoles = Math.max(200, porRole.length * 36);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavUsuarios isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
        <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-bold tracking-tight">Painel Admin · Dashboard de Usuários</span>
          </div>
          <button
            onClick={() => navigate("/inicio")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}
          >
            Menu Principal
          </button>
        </header>

        <main className="flex-1 px-6 py-8 max-w-6xl mx-auto w-full">
          {loading && <div className="py-16 text-center text-gray-400 text-sm">Carregando dados…</div>}
          {!loading && erro && <div className="py-16 text-center text-red-500 text-sm">{erro}</div>}

          {!loading && !erro && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <KpiCard icon={Users} label="Total de Usuários" value={resumo.total_usuarios ?? 0} cor="#7C3AED" />
                <KpiCard icon={UserCheck} label="Usuários Ativos" value={resumo.usuarios_ativos ?? 0} cor="#10B981" />
                <KpiCard icon={ShieldCheck} label="Logins Hoje" value={resumo.logins_hoje ?? 0} cor="#EC4899" />
                <KpiCard icon={Ban} label="Logins (7 dias)" value={resumo.logins_7_dias ?? 0} cor="#6366F1" />
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">
                  Logins por mês {resumo.ano_logins_por_mes ? `· ${resumo.ano_logins_por_mes}` : ""}
                </h3>
                <p className="text-xs text-gray-500 mb-4">Total de logins realizados em cada mês do ano corrente.</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={loginsPorMes} margin={{ left: 4, right: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94A3B8" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                    <Tooltip />
                    <Bar dataKey="total" name="Logins" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Usuários ativos na plataforma</h3>
                  <p className="text-xs text-gray-500 mb-4">Proporção de usuários ativos, bloqueados e inativos.</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={porStatus} dataKey="qtd" nameKey="status" innerRadius={55} outerRadius={85} paddingAngle={2}>
                        {porStatus.map((s) => (
                          <Cell key={s.status} fill={STATUS_CORES[s.status] || "#94A3B8"} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    {porStatus.map((s) => (
                      <div key={s.status} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: STATUS_CORES[s.status] || "#94A3B8" }} />
                        {s.status} ({s.qtd})
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Usuários por role</h3>
                  <p className="text-xs text-gray-500 mb-4">Quantidade de usuários cadastrados em cada perfil de acesso.</p>
                  <ResponsiveContainer width="100%" height={alturaGraficoRoles}>
                    <BarChart data={porRole} layout="vertical" margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                      <YAxis type="category" dataKey="role" width={140} tick={{ fontSize: 11, fill: "#475569" }} />
                      <Tooltip />
                      <Bar dataKey="qtd" name="Usuários" radius={[0, 4, 4, 0]}>
                        {porRole.map((r, idx) => (
                          <Cell key={r.role} fill={ROLE_CORES[idx % ROLE_CORES.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </main>

        <footer className="text-center text-xs text-gray-400 py-6">
          © {new Date().getFullYear()} Youx Group · Painel Administrativo
        </footer>
      </div>
    </div>
  );
}
